<template>
  <div class="container p-4">
    <div class="flex flex-wrap align-items-center gap-2 mb-3">
      <ButtonPrime icon="pi pi-arrow-left" class="p-button-text" @click="router.push({ name: 'estudyoo-hub' })" />
      <h3 class="text-xl font-bold m-0">Juegos Estudyoo</h3>
    </div>

    <DjangoAuthGate @authenticated="(ok) => (djangoOk = ok)" />

    <template v-if="djangoOk">
      <EstudyooLoadingBanner
        :visible="importando || descargandoPlantilla"
        :message="importando ? 'Importando preguntas desde Excel…' : 'Descargando plantilla…'"
      />
      <TabView>
        <TabPanel header="Preguntas">
          <div class="flex flex-wrap gap-2 mb-3">
            <ButtonPrime label="Recargar" icon="pi pi-refresh" :loading="loading" @click="cargarPreguntas" />
            <ButtonPrime label="Plantilla Excel" icon="pi pi-download" class="p-button-outlined" :loading="descargandoPlantilla" :disabled="importando" @click="bajarPlantilla" />
            <FileUpload
              mode="basic"
              choose-label="Importar Excel"
              accept=".xlsx,.xls"
              :auto="true"
              custom-upload
              :disabled="importando || descargandoPlantilla"
              @uploader="importar"
            />
          </div>
          <DataTable :value="preguntas" :loading="loading" class="p-datatable-sm" empty-message="Sin preguntas">
            <Column field="enunciado" header="Enunciado" />
            <Column field="capitulo_id" header="Capítulo ID" />
            <Column field="puntos" header="Pts" />
            <Column field="activa" header="Activa" />
          </DataTable>
        </TabPanel>
        <TabPanel header="Salas">
          <ButtonPrime label="Recargar salas" icon="pi pi-refresh" class="mb-3" :loading="loadingSalas" @click="cargarSalas" />
          <DataTable :value="salas" :loading="loadingSalas" class="p-datatable-sm">
            <Column field="nombre" header="Nombre" />
            <Column field="codigo" header="Código" />
            <Column field="curso_id" header="Curso" />
            <Column field="materia_id" header="Materia" />
          </DataTable>
        </TabPanel>
        <TabPanel header="Tipos de juego">
          <ButtonPrime label="Recargar tipos" icon="pi pi-refresh" class="mb-3" :loading="loadingTipos" @click="cargarTipos" />
          <DataTable :value="tipos" :loading="loadingTipos" class="p-datatable-sm">
            <Column field="codigo" header="Código" />
            <Column field="nombre" header="Nombre" />
          </DataTable>
        </TabPanel>
      </TabView>
    </template>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useToast } from 'primevue/usetoast';
import TabView from 'primevue/tabview';
import TabPanel from 'primevue/tabpanel';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import ButtonPrime from 'primevue/button';
import FileUpload from 'primevue/fileupload';
import DjangoAuthGate from '@/components/estudyoo/DjangoAuthGate.vue';
import EstudyooLoadingBanner from '@/components/estudyoo/EstudyooLoadingBanner.vue';
import { JuegosApi, descargarBlob } from '@/services/EstudyooAdminApi';
import { isDjangoAdminAuthenticated } from '@/services/djangoAdminAuth';

const router = useRouter();
const toast = useToast();
const djangoOk = ref(isDjangoAdminAuthenticated());
const preguntas = ref([]);
const salas = ref([]);
const tipos = ref([]);
const loading = ref(false);
const loadingSalas = ref(false);
const loadingTipos = ref(false);
const descargandoPlantilla = ref(false);
const importando = ref(false);

async function cargarPreguntas() {
  loading.value = true;
  try {
    preguntas.value = await JuegosApi.listPreguntas({ activa: 'true' });
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Error', detail: e.message, life: 4000 });
  } finally {
    loading.value = false;
  }
}

async function cargarSalas() {
  loadingSalas.value = true;
  try {
    salas.value = await JuegosApi.listSalas();
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Salas', detail: e.message, life: 4000 });
  } finally {
    loadingSalas.value = false;
  }
}

async function cargarTipos() {
  loadingTipos.value = true;
  try {
    tipos.value = await JuegosApi.listTiposJuego();
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Tipos', detail: e.message, life: 4000 });
  } finally {
    loadingTipos.value = false;
  }
}

async function bajarPlantilla() {
  descargandoPlantilla.value = true;
  try {
    const res = await JuegosApi.descargarPlantillaPreguntas();
    descargarBlob(res, 'plantilla-preguntas-juego.xlsx');
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Plantilla', detail: e.message, life: 5000 });
  } finally {
    descargandoPlantilla.value = false;
  }
}

async function importar(ev) {
  const file = ev.files[0];
  if (!file) return;
  importando.value = true;
  try {
    const r = await JuegosApi.importarPreguntas(file);
    toast.add({
      severity: r.ok ? 'success' : 'warn',
      summary: 'Importación',
      detail: `Creadas: ${r.creadas || 0}, actualizadas: ${r.actualizadas || 0}, errores: ${(r.errores || []).length}`,
      life: 6000,
    });
    await cargarPreguntas();
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Importación', detail: e.response?.data?.detail || e.message, life: 5000 });
  } finally {
    importando.value = false;
    ev?.options?.clear?.();
  }
}

onMounted(() => {
  if (djangoOk.value) {
    cargarPreguntas();
    cargarSalas();
    cargarTipos();
  }
});
</script>
